import { ScrollArea } from "@/components/ui/scroll-area";
import { Scale, ShieldCheck, FileText, Users, Lock, RefreshCw, Mail } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="container mx-auto py-12 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Scale className="h-12 w-12 text-primary" />
        </div>
        <h1 className="text-4xl font-heading font-bold text-gray-900 mb-4">Terms and Conditions</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Last Updated: April 20, 2025</p>
      </div>

      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md">
        <ScrollArea className="h-[600px] p-8">
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-6">
              Welcome to CauseConnect.ai ("CauseConnect"), operated by Cause Connect Pte. Ltd. CauseConnect connects Non-Profit Organizations ("NPOs"), Volunteers, and Corporates to create meaningful social impact. 
              By accessing or using CauseConnect, you agree to comply with these Terms. If you do not agree, you may not use CauseConnect.
            </p>

            {/* 1. Acceptance of Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing or using CauseConnect, you confirm that you have read, understood, and agree to be bound by these 
                Terms, our Privacy Policy, and other related policies. These Terms constitute a legally binding agreement between you and Cause Connect Pte. Ltd.
              </p>
            </section>

            {/* 2. User Roles & Responsibilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">2. User Roles and Responsibilities</h2>
              <h3 className="text-xl font-semibold mb-3">2.1. NPOs</h3>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Must ensure all event listings contain accurate details.</li>
                <li>Responsible for compliance with all local and international laws.</li>
                <li>Agree to refund or reschedule events in accordance with the Cancellation Policy.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2.2. Corporates</h3>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Must ensure accuracy in event registrations.</li>
                <li>Responsible for ensuring employees comply with event guidelines.</li>
              </ul>
            </section>

            {/* 3. Code of Conduct */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">3. Code of Conduct</h2>
              <ul className="list-disc pl-6 text-gray-600">
                <li>All users must maintain professional and respectful conduct.</li>
                <li>Discriminatory, harassing, or illegal behavior is strictly prohibited.</li>
              </ul>
            </section>

            {/* 4. Fees & Payments */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">4. Fees and Payments</h2>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Payments must be made via CauseConnect’s designated payment channels.</li>
              </ul>
            </section>

            {/* 5. Data Protection & Compliance */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-primary" /> Data Protection & Compliance
              </h2>
              <p className="text-gray-600 mb-4">
                We comply with best-in-class data protection standards to safeguard user data.
              </p>
              <ul className="list-disc pl-6 text-gray-600">
                <li>DSR Compliant: Users can access, modify, or delete their personal data upon request.</li>
                <li>Privacy by Design: We prioritize user data protection in every feature of CauseConnect.</li>
                <li>COPPA Compliant: We do not collect or process data from children under 13.</li>
              </ul>
            </section>

            {/* 6. Liability & Waivers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">6. Liability and Waivers</h2>
              <ul className="list-disc pl-6 text-gray-600">
                <li>Users agree to CauseConnect’s Liability Waiver & Photo Release terms.</li>
                <li>Cause Connect Pte. Ltd. shall not be held liable for any damages, injuries, or losses arising from events 
                organized through CauseConnect.</li>
                <li>Any modifications to these liability terms must be explicitly documented through a written amendment agreed upon by both parties.</li>
                <li>NPOs, Volunteers, and Corporates are responsible for ensuring adequate safety measures and carrying appropriate 
                insurance coverage where applicable.</li>
              </ul>
            </section>

            {/* 7. Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">7. Termination</h2>
              <p className="text-gray-600">
                CauseConnect reserves the right to terminate any account that violates these Terms.
              </p>
            </section>

            {/* 8. Dispute Resolution */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">8. Dispute Resolution</h2>
              <ul className="list-disc pl-6 text-gray-600">
                <li>All disputes must first go through mediation facilitated by CauseConnect.</li>
                <li>Unresolved disputes will be subject to arbitration in Singapore.</li>
              </ul>
            </section>

            {/* 9. Modifications to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-primary" /> Modifications to Terms
              </h2>
              <p className="text-gray-600">
                CauseConnect reserves the right to update these Terms at any time.
              </p>
            </section>

            {/* 10. Contact Information */}
            <section>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Mail className="w-6 h-6 text-primary" /> Contact Information
              </h2>
              <p className="text-gray-600">
                For any questions regarding these Terms, contact us at support@causeconnect.ai.
              </p>
            </section>

            <p className="text-gray-600 mt-8">By using CauseConnect, you acknowledge and agree to these Terms.</p>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
